// ── LeadToOpportunityPage — funnel step: Leads → Opportunities ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { PivotMatrix, KpiCard, pctLabel, parseDate, isExcludedOwner } from "./analyticsShared";

export const LeadToOpportunityPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Set (multi-select); null until initialised
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [empSel, setEmpSel] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("getOpportunities"), api.get("getLeads")])
      .then(([o, l]) => { setOpps(Array.isArray(o) ? o : []); setLeads(Array.isArray(l) ? l : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set([...opps, ...leads].map(o => o.ownerName).filter(Boolean))].sort(), [opps, leads]);
  const sources = useMemo(() => [...new Set([...opps, ...leads].map(o => o.lead_source).filter(Boolean))].sort(), [opps, leads]);

  // default the Sourced By multi-select to every owner EXCEPT the four excluded ones
  useEffect(() => {
    if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o))));
  }, [owners, ownerSel]);

  const fromD = created.from ? new Date(created.from) : null;
  const toD = created.to ? new Date(created.to) : null;
  const inCreated = (v) => { if (!fromD && !toD) return true; const d = parseDate(v); if (!d) return false; if (fromD && d < fromD) return false; if (toD && d > toD) return false; return true; };
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));
  const pass = (o) => {
    if (!ownerOk(o.ownerName)) return false;
    if (sourceSel && o.lead_source !== sourceSel) return false;
    return true;
  };

  const oppsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return opps.filter(o => pass(o) && !(c && !((o.companyname || o.name || "").toLowerCase().includes(c))) && inCreated(o.createdat));
  }, [opps, company, sourceSel, ownerSel, created]);
  const leadsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return leads.filter(l => pass(l) && !(c && !((l.companyname || "").toLowerCase().includes(c))) && inCreated(l.createdat));
  }, [leads, company, sourceSel, ownerSel, created]);

  // empSel highlights the matrices (which keep every employee) but filters the KPI + detail
  const oppsSel = useMemo(() => empSel ? oppsF.filter(o => (o.ownerName || "—") === empSel) : oppsF, [oppsF, empSel]);
  const leadsSel = useMemo(() => empSel ? leadsF.filter(l => (l.ownerName || "—") === empSel) : leadsF, [leadsF, empSel]);

  // detail grouped by (owner, company)
  const detArr = useMemo(() => {
    const map = new Map();
    const k = (owner, comp) => (owner || "—") + " " + (comp || "—");
    leadsSel.forEach(l => {
      const owner = l.ownerName || "—", comp = l.companyname || "—", key = k(owner, comp);
      const e = map.get(key) || { ownerName: owner, company: comp, leadCnt: 0, oppCnt: 0, stage: "", termination_reason: "", initial: 0 };
      e.leadCnt += 1; map.set(key, e);
    });
    oppsSel.forEach(o => {
      const owner = o.ownerName || "—", comp = o.companyname || o.name || "—", key = k(owner, comp);
      const e = map.get(key) || { ownerName: owner, company: comp, leadCnt: 0, oppCnt: 0, stage: "", termination_reason: "", initial: 0 };
      e.oppCnt += 1; e.initial += parseFloat(o.initial_funding_amount || 0);
      if (!e.stage) e.stage = o.stage || ""; if (!e.termination_reason) e.termination_reason = o.termination_reason || "";
      map.set(key, e);
    });
    return [...map.values()];
  }, [leadsSel, oppsSel]);

  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(detArr, 'company', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const convertedLeads = leadsSel.filter(l => l.converted_at || l.converted_accountid);
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const pick = (k) => setEmpSel(prev => prev === k ? "" : k);
  const rowStyle = { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" };
  const toggleOwner = (o) => setOwnerSel(prev => { const n = new Set(prev || []); n.has(o) ? n.delete(o) : n.add(o); return n; });

  return <>
    <SectionHeader title="Lead to Opportunity" />

    <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "stretch" }}>
      {/* filters — two stacked cards, left */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 180 }} />
        <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <div style={{ position: "relative" }}>
          <Btn onClick={() => setOwnerOpen(v => !v)}>Sourced By{ownerSel ? " (" + ownerSel.size + ")" : ": All"} ▾</Btn>
          {ownerOpen && (
            <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, marginTop: 4, background: "#fff", border: "1px solid var(--color-border-secondary)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 8, maxHeight: 320, overflowY: "auto", minWidth: 210 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
                <button onClick={() => setOwnerSel(new Set(owners))} style={{ fontSize: 11, background: "none", border: "none", color: B.cyan, cursor: "pointer" }}>Select all</button>
                <button onClick={() => setOwnerSel(new Set())} style={{ fontSize: 11, background: "none", border: "none", color: B.cyan, cursor: "pointer" }}>Clear</button>
                <button onClick={() => setOwnerOpen(false)} style={{ fontSize: 11, background: "none", border: "none", color: "var(--color-text-secondary)", cursor: "pointer" }}>Close</button>
              </div>
              {owners.map(o => (
                <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 2px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={ownerSel ? ownerSel.has(o) : !isExcludedOwner(o)} onChange={() => toggleOwner(o)} /> {o}
                </label>
              ))}
            </div>
          )}
        </div>
        {empSel && <button onClick={() => setEmpSel("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{empSel} <span style={{ fontWeight: 700 }}>✕</span></button>}
        </Card>
        <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
            Created
            <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
            <span>–</span>
            <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
          </label>
        </Card>
      </div>
      {/* KPI — standalone card, right */}
      <div style={{ flex: "0 0 240px" }}>
        <KpiCard label="Lead → Opp Conversion" value={pctLabel(convertedLeads.length, leadsSel.length)} sub={convertedLeads.length + " of " + leadsSel.length + " leads"} />
      </div>
    </div>

    {/* full-width matrices */}
    <PivotMatrix title="Leads (by created month)" items={leadsF} rowKeyOf={l => l.ownerName} dateOf={l => l.createdat} onRowClick={pick} activeRow={empSel} />
    <PivotMatrix title="Opportunities (by created month)" items={oppsF} rowKeyOf={o => o.ownerName} dateOf={o => o.createdat} onRowClick={pick} activeRow={empSel} />

    {/* detail at the bottom, full width */}
    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span>Detail — Lead &amp; Opp counts by Company</span>
        <ExportBtn onClick={() => exportCSV(detailSorted, ["ownerName", "company", "leadCnt", "oppCnt", "stage", "termination_reason", "initial"], "lead_to_opportunity.csv")}>Export CSV</ExportBtn>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <STh label="Sourced By"      sortKey="ownerName"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Company / Account" sortKey="company"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lead Cnt"        sortKey="leadCnt"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Opp Cnt"         sortKey="oppCnt"             currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Stage"           sortKey="stage"              currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Termination Reason" sortKey="termination_reason" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Initial Amount"  sortKey="initial"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        </tr></thead>
        <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={7} style={{ color: "var(--color-text-secondary)" }}>No records.</Td></tr> : detailSorted.map((r, i) => (
          <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td>{r.ownerName}</Td>
            <Td style={{ fontWeight: 500 }}>{r.company}</Td>
            <TdNum>{r.leadCnt || ""}</TdNum>
            <TdNum>{r.oppCnt || ""}</TdNum>
            <Td>{r.stage || "—"}</Td>
            <Td>{r.termination_reason || "—"}</Td>
            <TdNum>{r.initial ? fmt(r.initial) : ""}</TdNum>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={2} style={{ padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailSorted.reduce((s, r) => s + r.leadCnt, 0)}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailSorted.reduce((s, r) => s + r.oppCnt, 0)}</td>
          <td colSpan={2} style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, r) => s + r.initial, 0))}</td>
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
